import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  clientDetailDtoSchema,
  clientDtoSchema,
  clientListDtoSchema,
  type ClientDetailDto,
  type ClientDto,
  type ClientListDto,
} from '@lib/dto/client.dto'
import { contactDtoSchema, type ContactDto } from '@lib/dto/contact.dto'
import { siteDtoSchema, type SiteDto } from '@lib/dto/site.dto'
import {
  areaDtoSchema,
  areaListDtoSchema,
  type AreaDto,
  type AreaListDto,
} from '@lib/dto/area.dto'
import type {
  CreateClientInput,
  ListClientsQuery,
  UpdateClientInput,
} from '@lib/input/client.input'
import type {
  CreateContactInput,
  UpdateContactInput,
} from '@lib/input/contact.input'
import type { CreateSiteInput, UpdateSiteInput } from '@lib/input/site.input'
import type { CreateAreaInput, UpdateAreaInput } from '@lib/input/area.input'
import type { IClientService } from '@/types/ClientService.types'

/** Build a query string, omitting empty filters so the URL stays readable. */
function toQueryString(query: Partial<ListClientsQuery>): string {
  const params = new URLSearchParams()
  if (query.search) params.set('search', query.search)
  if (query.type) params.set('type', query.type)
  if (query.status) params.set('status', query.status)
  if (query.includeArchived) params.set('includeArchived', 'true')
  const encoded = params.toString()
  return encoded === '' ? '' : `?${encoded}`
}

/**
 * Service for the client REST endpoints, including the nested contact and site
 * routes. Responses are validated against the shared `@lib` schemas at the
 * boundary, so an API/web drift surfaces as a clean error here rather than a
 * `TypeError` deeper in the UI.
 */
class ClientService implements IClientService {
  async list(query: Partial<ListClientsQuery> = {}): Promise<ClientListDto> {
    try {
      return await api.get(
        `/clients${toQueryString(query)}`,
        clientListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list clients', { error: toErrorMessage(error) })
      throw error
    }
  }

  async get(id: string): Promise<ClientDetailDto> {
    try {
      return await api.get(
        `/clients/${encodeURIComponent(id)}`,
        clientDetailDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load client', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async create(input: CreateClientInput): Promise<ClientDto> {
    try {
      return await api.post('/clients', input, clientDtoSchema)
    } catch (error) {
      logger.error('Failed to create client', { error: toErrorMessage(error) })
      throw error
    }
  }

  async update(id: string, input: UpdateClientInput): Promise<ClientDto> {
    try {
      return await api.put(
        `/clients/${encodeURIComponent(id)}`,
        input,
        clientDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update client', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setArchived(id: string, archived: boolean): Promise<ClientDto> {
    const action = archived ? 'archive' : 'restore'
    try {
      return await api.post(
        `/clients/${encodeURIComponent(id)}/${action}`,
        {},
        clientDtoSchema,
      )
    } catch (error) {
      logger.error(`Failed to ${action} client`, {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async addContact(
    clientId: string,
    input: CreateContactInput,
  ): Promise<ContactDto> {
    try {
      return await api.post(
        `/clients/${encodeURIComponent(clientId)}/contacts`,
        input,
        contactDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to add contact', {
        clientId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async updateContact(
    id: string,
    input: UpdateContactInput,
  ): Promise<ContactDto> {
    try {
      return await api.put(
        `/contacts/${encodeURIComponent(id)}`,
        input,
        contactDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update contact', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setContactArchived(id: string, archived: boolean): Promise<ContactDto> {
    const action = archived ? 'archive' : 'restore'
    try {
      return await api.post(
        `/contacts/${encodeURIComponent(id)}/${action}`,
        {},
        contactDtoSchema,
      )
    } catch (error) {
      logger.error(`Failed to ${action} contact`, {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async getSite(id: string): Promise<SiteDto> {
    try {
      return await api.get(`/sites/${id}`, siteDtoSchema)
    } catch (error) {
      logger.error('Failed to load site', { id, error: toErrorMessage(error) })
      throw error
    }
  }

  async addSite(clientId: string, input: CreateSiteInput): Promise<SiteDto> {
    try {
      return await api.post(
        `/clients/${encodeURIComponent(clientId)}/sites`,
        input,
        siteDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to add site', {
        clientId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async updateSite(id: string, input: UpdateSiteInput): Promise<SiteDto> {
    try {
      return await api.put(
        `/sites/${encodeURIComponent(id)}`,
        input,
        siteDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update site', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setSiteArchived(id: string, archived: boolean): Promise<SiteDto> {
    const action = archived ? 'archive' : 'restore'
    try {
      return await api.post(
        `/sites/${encodeURIComponent(id)}/${action}`,
        {},
        siteDtoSchema,
      )
    } catch (error) {
      logger.error(`Failed to ${action} site`, {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async listAreas(siteId: string): Promise<AreaListDto> {
    try {
      return await api.get(
        `/sites/${encodeURIComponent(siteId)}/areas`,
        areaListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list areas', {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async addArea(siteId: string, input: CreateAreaInput): Promise<AreaDto> {
    try {
      return await api.post(
        `/sites/${encodeURIComponent(siteId)}/areas`,
        input,
        areaDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to add area', {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async updateArea(id: string, input: UpdateAreaInput): Promise<AreaDto> {
    try {
      return await api.put(
        `/areas/${encodeURIComponent(id)}`,
        input,
        areaDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update area', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async removeArea(id: string): Promise<void> {
    try {
      await api.del(`/areas/${encodeURIComponent(id)}`)
    } catch (error) {
      logger.error('Failed to remove area', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const clientService = new ClientService()
